import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCartStore, useUIStore } from "../store/cartStore";
import { useAuth } from "../contexts/AuthContext";
import Logo from "../assets/images/logo.png";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getCartItemCount } = useCartStore();
  const { isMobileMenuOpen, toggleMobileMenu, closeMobileMenu } = useUIStore();
  const { user, isAuthenticated, logout } = useAuth();

  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);

  const [scrolled, setScrolled] = useState(false);

  // Fade/blur background when page scrolls
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile/profile menus on route change
  useEffect(() => {
    closeMobileMenu();
    setIsProfileMenuOpen(false);
  }, [location.pathname, closeMobileMenu]);

  const handleLogout = () => {
    logout();
    setIsProfileMenuOpen(false);
    closeMobileMenu();
    navigate("/login");
  };

  // Get dashboard route based on user role
  const getDashboardRoute = () => {
    if (!user?.role) return "/profile";
    const dashboardMap = {
      admin: "/admin",
      sales: "/sales-dashboard",
      leads: "/leads-dashboard",
      orders: "/orders-dashboard",
      telecaller: "/telecaller-dashboard",
      customer: "/profile",
    };
    return dashboardMap[user.role.toLowerCase()] || "/profile";
  };

  const isActive = (path) => location.pathname === path;

  // Navigation links
  const publicLinks = [
    { name: "Products", path: "/" },
    { name: "Book Consultation", path: "/appointments" },
    { name: "Quick Sales Call", path: "/register" },
  ];

  const staffRoles = ["admin", "sales", "orders", "telecaller"];
  const isStaffUser =
    isAuthenticated && user?.role && staffRoles.includes(user.role.toLowerCase());

  if (isStaffUser) {
    publicLinks.push({ name: "Meeting Dashboard", path: "/calendly-dashboard" });
  }

  const navLinks = isAuthenticated
    ? publicLinks
    : [...publicLinks, { name: "Login", path: "/login" }];

  const cartItemCount = getCartItemCount();

  // 🎨 Lavender palette (adjust if you like)
  const LAVENDER = "text-fuchsia-800";        // base lavender (violet-400-ish)
  const LAVENDER_HOVER = "hover:text-fuchsia-500"; // deeper on hover (violet-500)
  const LAVENDER_ACTIVE = "text-fuchsia-900"; // active (violet-600)

  // ✨ Animated underline (center-out) using scaleX + spring-ish cubic-bezier
  const underlineSpring =
    "after:content-[''] after:absolute after:left-1/2 after:-translate-x-1/2 " +
    "after:-bottom-0.5 after:h-[2px] after:bg-current after:rounded-full " +
    "after:w-[88%] after:origin-center after:scale-x-0 " +
    "after:transition-transform after:duration-500 after:ease-[cubic-bezier(0.16,1,0.3,1)] " + // springy
    "group-hover:after:scale-x-100";

  // Desktop link classes
  const linkBase =
    "relative group px-3 py-2 text-sm font-medium transition-colors duration-200 ease-in-out";
  const linkInactive = `${LAVENDER} ${LAVENDER_HOVER} ${underlineSpring}`;
  const linkActive =
    `${LAVENDER_ACTIVE} ` +
    "after:content-[''] after:absolute after:left-1/2 after:-translate-x-1/2 " +
    "after:-bottom-0.5 after:h-[2px] after:bg-current after:rounded-full " +
    "after:w-[88%] after:origin-center after:scale-x-100 " + // keep visible
    "after:transition-transform after:duration-500 after:ease-[cubic-bezier(0.16,1,0.3,1)]";

  // Mobile link classes (bigger text, same effect)
  const mobileLinkBase =
    "relative group block px-3 py-2 text-base font-medium transition-colors duration-200 ease-in-out";
  const mobileInactive = `${LAVENDER} ${LAVENDER_HOVER} ${underlineSpring}`;
  const mobileActive =
    `${LAVENDER_ACTIVE} ` +
    "after:content-[''] after:absolute after:left-1/2 after:-translate-x-1/2 " +
    "after:-bottom-0.5 after:h-[2px] after:bg-current after:rounded-full " +
    "after:w-[92%] after:origin-center after:scale-x-100 " +
    "after:transition-transform after:duration-500 after:ease-[cubic-bezier(0.16,1,0.3,1)]";

  return (
    <>
      <nav
        className={[
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          scrolled
            ? "bg-white/80 backdrop-blur-md shadow-md border-b border-gray-200"
            : "bg-transparent",
        ].join(" ")}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-18">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center" onClick={closeMobileMenu}>
                <div className="w-40 items-center justify-center mr-3">
                  <img src={Logo} alt="Logo" />
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => {
                const active = isActive(link.path);
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    aria-current={active ? "page" : undefined}
                    className={`${linkBase} ${active ? linkActive : linkInactive}`}
                  >
                    {link.name}
                  </Link>
                );
              })}
            </div>

            {/* Right side items */}
            <div className="flex items-center space-x-4">
              {/* Cart Button */}
              <Link
                to="/cart"
                className={`relative p-2 ${LAVENDER} ${LAVENDER_HOVER} transition-colors`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                  />
                </svg>
                {cartItemCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-fuchsia-800 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {cartItemCount > 99 ? "99+" : cartItemCount}
                  </span>
                )}
              </Link>

              {/* User Profile Dropdown (Desktop) */}
              {isAuthenticated && (
                <div className="hidden md:block relative" ref={profileMenuRef}>
                  <button
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    className={`flex items-center space-x-2 p-2 rounded-md transition-colors ${LAVENDER} ${LAVENDER_HOVER}`}
                  >
                    <div className="w-8 h-8 bg-fuchsia-800 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">
                        {user?.name?.charAt(0).toUpperCase() || "U"}
                      </span>
                    </div>
                    <span className="text-sm font-medium max-w-32 truncate">{user?.name}</span>
                    <svg
                      className={`w-4 h-4 transition-transform ${isProfileMenuOpen ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isProfileMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                      <div className="px-4 py-3 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                        <p className="text-xs text-fuchsia-800 font-medium mt-1 capitalize">{user?.role}</p>
                      </div>

                      <Link
                        to={getDashboardRoute()}
                        onClick={() => setIsProfileMenuOpen(false)}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:text-fuchsia-800 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                        Dashboard
                      </Link>

                      <Link
                        to="/profile"
                        onClick={() => setIsProfileMenuOpen(false)}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:text-fuchsia-800 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        My Profile
                      </Link>

                      {user?.role === "customer" && (
                        <Link
                          to="/my-orders"
                          onClick={() => setIsProfileMenuOpen(false)}
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:text-fuchsia-800 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                          </svg>
                          My Orders
                        </Link>
                      )}

                      <Link
                        to="/settings"
                        onClick={() => setIsProfileMenuOpen(false)}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:text-fuchsia-800 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Settings
                      </Link>

                      <div className="border-t border-gray-200 mt-1" />

                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:text-red-700 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Mobile menu button */}
              <button
                onClick={toggleMobileMenu}
                className={`md:hidden p-2 rounded-md transition-colors ${LAVENDER} ${LAVENDER_HOVER}`}
                aria-label="Toggle menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 py-4 bg-white/90 backdrop-blur">
              {isAuthenticated && (
                <div className="px-3 py-3 mb-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-[#7C3AED] rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold">
                        {user?.name?.charAt(0).toUpperCase() || "U"}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                      <p className="text-xs text-fuchsia-800 font-medium capitalize">{user?.role}</p>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Link
                      to={getDashboardRoute()}
                      onClick={closeMobileMenu}
                      className={`${mobileLinkBase} ${mobileInactive}`}
                    >
                      Dashboard
                    </Link>

                    <Link
                      to="/profile"
                      onClick={closeMobileMenu}
                      className={`${mobileLinkBase} ${mobileInactive}`}
                    >
                      My Profile
                    </Link>

                    {user?.role === "customer" && (
                      <Link
                        to="/my-orders"
                        onClick={closeMobileMenu}
                        className={`${mobileLinkBase} ${mobileInactive}`}
                      >
                        My Orders
                      </Link>
                    )}

                    <Link
                      to="/settings"
                      onClick={closeMobileMenu}
                      className={`${mobileLinkBase} ${mobileInactive}`}
                    >
                      Settings
                    </Link>

                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-3 py-2 text-sm text-red-600 hover:text-red-700 rounded-md transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Logout
                    </button>
                  </div>
                </div>
              )}

              {/* Mobile nav links */}
              <div className="space-y-2">
                {navLinks.map((link) => {
                  const active = isActive(link.path);
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={closeMobileMenu}
                      aria-current={active ? "page" : undefined}
                      className={`${mobileLinkBase} ${active ? mobileActive : mobileInactive}`}
                    >
                      {link.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Spacer to offset the fixed 64px header (h-16) */}
      <div className="h-16" />
    </>
  );
};

export default Navbar;
